import { initializeComponentRegistry } from '@owc/component-registry-connection';
import { AemComponentData } from '../../../utils/aem.util';
import { defineComponent, getCurrentInstance, h, onMounted, provide, ref, Transition, VNode, watch } from 'vue';
import { Logger } from '../../../utils/logger.util';
import { findParentShadowRoot } from '../../../utils/query-element.util';
import styles from './dataLoader.scss?inline';

const NAME = 'DataLoader';

/**
 * Factory function that creates a dynamic component
 * which will render a "loading state" component while data is being
 * fetched in the background via the `dataLoadFn` option.
 * Once the data has been loaded the `MainComponent` will be rendered.
 */
export const createDataLoaderComponent = ({
  LoadingComponent,
  loadingComponentProps,
  MainComponent,
  ErrorComponent,
  errorComponentProps,
  dataLoadFn,
}: {
  // The loading component that should be rendered while data is being loaded
  LoadingComponent?: ReturnType<typeof defineComponent>;
  // Optional props to pass to the LoadingComponent
  loadingComponentProps?: Record<string, unknown>;
  // The actual component to render after data has been loaded
  MainComponent: ReturnType<typeof defineComponent>;
  // Error component to show if an error is thrown during data load & initialization
  ErrorComponent?: ReturnType<typeof defineComponent>;
  // Optional props to pass to the ErrorComponent
  errorComponentProps?: Record<string, unknown>;
  // Data loading function that must return a promise. While the promise is pending
  // the "LoadingComponent" will be rendered.
  dataLoadFn: (aemComponentData?: AemComponentData) => Promise<unknown>;
}) =>
  defineComponent({
    name: NAME,
    props: {
      datajson: {
        type: String,
        required: false,
        default: undefined,
      },
      componentId: {
        type: String,
        required: false,
        default: undefined,
      },
    },
    setup(props, ctx) {
      const isReady = ref(false);
      const isError = ref(false);
      const aemComponentData = ref<AemComponentData>();

      onMounted(() => {
        const instance = getCurrentInstance();

        if (!instance?.vnode.el) return;

        // Add style to shadowRoot manually since this is not a Vue SFC and thus not done automatically
        const shadowRoot = findParentShadowRoot(instance.vnode.el as HTMLElement);

        if (shadowRoot && !shadowRoot.querySelector(`style[data-vue-comp="${NAME}"]`)) {
          try {
            const styleElem = document.createElement('style');

            styleElem.setAttribute('data-vue-comp', NAME);
            styleElem.innerHTML = styles;
            shadowRoot.prepend(styleElem);
          } catch (error) {
            console.warn(error);
          }
        }
      });

      const initialize = async (componentData?: AemComponentData) => {
        try {
          await dataLoadFn(componentData);
          isReady.value = true;
        } catch (error) {
          isError.value = true;
          Logger.error('dataLoaderComponent: failed to initialize app', error);
        }
      };

      if (props.datajson || props.componentId) {
        const unwatch = watch(
          aemComponentData,
          () => {
            if (aemComponentData.value) {
              initialize(aemComponentData.value);
              provide('aemComponentData', aemComponentData);
              // Timeout to avoid: "Cannot access 'unwatch' before initialization"
              setTimeout(() => {
                unwatch();
              }, 0);
            }
          },
          { immediate: true },
        );

        if (props.datajson) {
          watch(
            () => props.datajson,
            () => {
              aemComponentData.value = JSON.parse(props.datajson as string);
            },
            { immediate: true },
          );
        } else if (props.componentId) {
          initializeComponentRegistry().subscribe(props.componentId, (data) => {
            aemComponentData.value = data as unknown as AemComponentData;
          });
        }
      } else {
        initialize();
      }

      return () => {
        let vnode: VNode | undefined;

        if (isReady.value) {
          // Backwards compatibility: JSONify AEM component data
          const datajson = JSON.stringify(aemComponentData.value);

          vnode = h(MainComponent, { ...ctx.attrs, datajson, key: 'main' });
        } else if (isError.value && ErrorComponent) {
          vnode = h(ErrorComponent, { ...errorComponentProps, key: 'error' });
        } else if (LoadingComponent) {
          vnode = h(LoadingComponent, { ...loadingComponentProps, key: 'loading' });
        }

        // return vnode;

        return h(
          Transition,
          {
            name: 'data-loader-replace',
            mode: 'default',
          },
          () => [vnode],
        );
      };
    },
  });
